import React from 'react';
import type { MobilityRoutine } from '../../types';
import { formatCardDateShort } from '../../utils/dateUtils';
// Apr 29, 2026 (Recal Health Round 2, Patch H) — `isToday` from
// sharedFormatters parses YYYY-MM-DD strings as local date (no UTC
// shift). Pre-fix this card used `new Date(scheduledDate).toDateString()
// === new Date().toDateString()` which UTC-parses bare date strings
// and shifts the comparison by the user's tz offset. Reproducible
// at any timezone with non-zero offset; surfaces as workouts
// rendering under the wrong day-of-week label.
import { isToday as isDateToday } from '../../utils/sharedFormatters';
import { ContextualChatButton } from '../ContextualChatButton';
import { getWorkoutIconType, WorkoutIcon } from '../../utils/workoutIconMapper';
// Icon imports - Not used directly, only for reference
// Actual icons come from WorkoutIcon component with SVG files
import { MobilityIcon, YogaIcon } from '../icons';
// Card system styles
import '../../styles/card-system.css';

// Import WORKOUT Anima components (same as SimpleWorkoutCard) for consistent layout
import { CmpWorkoutcard as AnimaWorkoutCard } from '../../anima/Cards/Cards-Live-Workout/src/components/CmpWorkoutcard/CmpWorkoutcard';
import { CmpWorkoutcard as AnimaArchivedWorkoutCard } from '../../anima/Cards/Cards-Archived-Workout/src/components/CmpWorkoutcard/CmpWorkoutcard';

interface SimpleMobilityCardProps {
  routine: MobilityRoutine;
  isArchived?: boolean;
  onTap?: () => void;
  onLongPress?: () => void;
  onComplete?: (routine: MobilityRoutine) => void;
  showTodayBadge?: boolean;
  showDateBadge?: boolean;
}

export const SimpleMobilityCard: React.FC<SimpleMobilityCardProps> = ({ 
  routine, 
  isArchived = false, 
  onTap, 
  onLongPress, 
  onComplete, 
  showTodayBadge = false, 
  showDateBadge = false 
}) => {
  
  const getSimplifiedMobilityCategory = (type: string, context: string) => {
    // Simplified categorization for better UX - only one primary tag per routine
    if (context === 'daily') {
      // Determine more specific daily activity type
      let activityType = 'Movement';
      if (type.includes('yoga')) activityType = 'Yoga';
      else if (type.includes('stretch')) activityType = 'Stretch';
      else if (type.includes('mobility')) activityType = 'Mobility';
      
      return { 
        label: `Daily ${activityType}`, 
        color: 'var(--cedar-primary)',
        bgColor: 'rgba(31, 108, 79, 0.1)',
        Icon: MobilityIcon
      };
    }
    
    if (type.includes('yoga') || type === 'yoga') {
      return { 
        label: 'Yoga', 
        color: 'var(--coloryogatxt)',
        bgColor: 'rgba(120, 52, 181, 0.1)',
        Icon: YogaIcon
      };
    }
    
    if (type.includes('static') || type.includes('stretch') || type.includes('recovery')) {
      return { 
        label: 'Stretch', 
        color: 'var(--cedar-primary)',
        bgColor: 'rgba(31, 108, 79, 0.1)',
        Icon: MobilityIcon // Uses MobilityIcon for card, WorkoutIcon component handles week view
      };
    }
    
    if (type.includes('dynamic') || type.includes('warmup')) {
      return { 
        label: 'Warmup', 
        color: 'var(--cedar-accent-sky)',
        bgColor: 'rgba(66, 165, 165, 0.1)',
        Icon: MobilityIcon
      };
    }

    if (type.includes('cooldown')) {
      return { 
        label: 'Cooldown', 
        color: 'var(--cedar-accent-sky)',
        bgColor: 'rgba(66, 165, 165, 0.1)',
        Icon: MobilityIcon
      };
    }

    if (type.includes('rehab')) {
      return { 
        label: 'Rehabilitation', 
        color: 'var(--colorrehabtxt)',
        bgColor: 'rgba(201, 45, 32, 0.1)',
        Icon: MobilityIcon, // Uses MobilityIcon for card, WorkoutIcon component handles week view
        description: 'Therapeutic exercises for injury recovery and pain relief'
      };
    }

    if (type.includes('corrective')) {
      return { 
        label: 'Corrective Exercise', 
        color: 'var(--cedar-primary)',
        bgColor: 'rgba(31, 108, 79, 0.1)',
        Icon: MobilityIcon,
        description: 'Targeted exercises to address movement imbalances'
      };
    }

    if (type.includes('recovery')) {
      return { 
        label: 'Recovery', 
        color: 'var(--cedar-primary)',
        bgColor: 'rgba(31, 108, 79, 0.1)',
        Icon: MobilityIcon
      };
    }
    
    // Default fallback
    return { 
      label: 'Mobility', 
      color: 'var(--cedar-primary)',
      bgColor: 'rgba(31, 108, 79, 0.1)',
      Icon: MobilityIcon
    };
  };

  // Use modality field from database (contains: yoga, static-stretching, joint-mobility, cooldown, etc.)
  const category = getSimplifiedMobilityCategory(routine.modality || routine.type || 'static', routine.context || 'standalone');
  const isToday = routine.scheduledDate && isDateToday(routine.scheduledDate);

  const AnimaComponent = isArchived ? AnimaArchivedWorkoutCard : AnimaWorkoutCard;

  // Format duration like workout cards (e.g., "30m", "1h 15m")
  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  // Get icon type based on modality (matches week view logic)
  const getIconType = () => {
    const modality = (routine.modality || routine.type || '').toLowerCase();
    if (modality === 'yoga' || modality.includes('yoga')) return 'yoga';
    if (modality === 'static-stretching' || modality.includes('stretch')) return 'stretching';
    return 'stretching'; // Default to stretching for mobility
  };

  // Format subcategory for pill (e.g., "Yoga", "Stretch")
  const formatSubcategory = () => {
    const modality = (routine.modality || routine.type || '').toLowerCase();
    if (modality === 'yoga' || modality.includes('yoga')) return 'Yoga';
    if (modality === 'static-stretching' || modality.includes('stretch')) return 'Stretch';
    if (modality.includes('cooldown')) return 'Cooldown';
    if (modality.includes('warmup')) return 'Warmup';
    return 'Mobility';
  };

  // Get day of week from date (accepts Date or string for scheduledDate)
  const getDayOfWeek = (dateInput?: string | Date) => {
    if (dateInput == null) return '';
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  // Determine state (matches SimpleWorkoutCard logic)
  const state = routine.status === 'completed' ? 'done' : 'live';

  const handleComplete = () => {
    // Check if this routine is scheduled for today (uses canonical
    // local-tz isDateToday helper; pre-Patch-H this used
    // toDateString() with UTC-parsed bare date strings, which
    // shifted the comparison by the user's tz offset).
    const isTodayLocal = routine.scheduledDate && isDateToday(routine.scheduledDate);

    // Only allow completion if it's today's routine (or no date set for flexibility, or daily routines)
    if (routine.scheduledDate && !isTodayLocal && routine.context !== 'daily') {
      return;
    }
    
    if (onComplete) {
      onComplete(routine);
    }
  };

  // Render checkmark indicator (matches SimpleWorkoutCard)
  const renderCheckmark = () => {
    if (routine.status === 'completed') {
      return (
        <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center" title="Completed">
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    }
    
    return (
      <div className="w-5 h-5 border-2 border-gray-300 rounded-full flex items-center justify-center" title="Not completed">
        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    );
  };

  // Enhanced shadow for browse view
  const cardShadow = showDateBadge ? 'shadow-lg' : 'shadow-sm';

  return (
    <div className={`relative mypath-card ${cardShadow}`} style={{ minHeight: '140px', backgroundColor: 'var(--colorsurface-50)', border: '1px solid var(--cedar-limestone)' }}>
      <div
        onClick={onTap}
        onContextMenu={(e) => {
          e.preventDefault();
          onLongPress && onLongPress();
        }}
        className="cursor-pointer relative"
        style={{ minHeight: '140px', paddingBottom: '52px' }}
      >
        {/* Anima component completely hidden - only used for state management */}
        <div className="absolute inset-0 opacity-0 pointer-events-none overflow-hidden" style={{ visibility: 'hidden' }}>
          <AnimaComponent
            state={state}
            text={getDayOfWeek(routine.scheduledDate)}
            text1={getDayOfWeek(routine.scheduledDate)}
            text2={getDayOfWeek(routine.scheduledDate)}
            text3={routine.title}
          />
        </div>

        {/* HEADER ROW: Date (left) | Category (center) | Checkmark (right of category) | Chat (right of checkmark) */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-center px-3 py-2 z-20 rounded-t-lg border-b" style={{ 
          backgroundColor: 'var(--colorsurface-50)', 
          borderColor: 'var(--cedar-limestone)',
          minHeight: '44px' // Ensure consistent header height
        }}>
          {/* Date - Always show on left if we have a date (Browse/Weekly/Today views) */}
          {routine.scheduledDate && (showDateBadge || showTodayBadge) ? (
            <div className="mypath-card-label font-medium px-2 py-1 rounded flex-shrink-0 flex items-center">
              {formatCardDateShort(typeof routine.scheduledDate === 'string' ? routine.scheduledDate : routine.scheduledDate.toISOString().split('T')[0])}
            </div>
          ) : (
            <div className="flex-shrink-0 w-16" /> 
          )}

          {/* Category Badge - Center */}
          <div className="flex-1 flex justify-center items-center">
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap" style={{ 
              backgroundColor: category.bgColor, 
              color: category.color 
            }}>
              <category.Icon size={14} color={category.color} />
              {formatSubcategory()}
            </div>
          </div>

          {/* Completion Checkmark - Right of Category */}
          <div className="flex-shrink-0 mx-2 flex items-center">
            {renderCheckmark()}
          </div>

          {/* Chat with Cedar - Right */}
          <div className="flex-shrink-0 flex items-center">
            <ContextualChatButton
              context={{
                type: 'mobility',
                id: routine.id,
                title: routine.title,
                description: routine.description || ''
              }}
              text=""
              size="small"
              variant="clean"
            />
          </div>
        </div>

        {/* TITLE SECTION: Icon (left, vertically centered in space) | Title (left-justified) */}
        <div className="absolute top-12 left-0 right-0 px-3 pt-2 pb-2 z-20 bg-white" style={{ minHeight: '60px', maxHeight: '72px' }}>
          <div className="flex items-center gap-3 h-full">
            {/* Mobility Icon - Left (vertically centered in container) */}
            <div className="flex-shrink-0">
              <WorkoutIcon 
                iconType={getIconType()} 
              />
            </div>

            {/* Title Text - Left-justified, smaller if 2 lines, centered vertically */}
            <div className="flex-1 flex items-center py-1">
              {(() => {
                const displayTitle = routine.title || 'Mobility Session';
                const fontSize = displayTitle.length > 30 ? '14px' : '16px';
                return (
                  <div 
                    className="font-semibold text-gray-900 line-clamp-2 break-words text-left"
                    style={{
                      fontSize,
                      lineHeight: '1.3'
                    }}
                  >
                    {displayTitle}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* POST-COMPLETION METRICS: movements count + target areas */}
        {routine.status === 'completed' && (() => {
          const movementCount = (routine.poses?.length || 0) + (routine.exercises?.length || 0);
          const areas = routine.targetAreas?.slice(0, 2) || [];
          if (movementCount === 0 && areas.length === 0) return null;
          return (
            <div className="absolute bottom-10 left-0 right-0 px-3 z-20">
              <div className="flex items-center gap-3 justify-center">
                {movementCount > 0 && (
                  <div className="text-center">
                    <div className="text-[10px] text-gray-400 uppercase">Movements</div>
                    <div className="text-xs font-semibold text-gray-500">{movementCount}</div>
                  </div>
                )}
                {areas.length > 0 && (
                  <div className="text-center">
                    <div className="text-[10px] text-gray-400 uppercase">Focus</div>
                    <div className="text-xs font-semibold text-gray-500">{areas.join(', ')}</div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* FOOTER ROW: Duration (left) */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-between items-center px-3 py-2 z-20 bg-white rounded-b-lg">
          {/* Duration - Left */}
          {routine.duration ? (
            <div className="bg-gray-800 text-white px-2 py-1 rounded text-xs font-medium inline-flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {formatDuration(typeof routine.duration === 'number' ? routine.duration : parseInt(routine.duration, 10))}
            </div>
          ) : (
            <div />
          )}
        </div>
      </div>
    </div>
  );
}; 